import { create } from 'zustand';
import { appService, requestService, appCategoryService } from './appStoreService';
import type { App as ApiApp, AppRequest as ApiAppRequest, AppCategory } from './types';

// Legacy interface for compatibility with existing components
interface App {
  id: string;
  name: string;
  description: string;
  fullDescription?: string;
  category: string;
  rating: number;
  reviewCount: number;
  downloads: number;
  accessType: 'Free' | 'Free Trial' | 'Paid';
  price?: number;
  status: 'Not Installed' | 'Installed';
  trialExpiresAt?: string;
  coverImage: string;
  screenshots?: string[];
  launchUrl?: string;
  isHighlighted?: boolean;
  isActive?: boolean;
  lastUpdated: string;
  version?: string;
  size?: string;
  developer?: string;
  features?: string[];
  requirements?: string[];
}

interface AppRequest {
  id: string;
  appName: string;
  category: string;
  description: string;
  businessJustification: string;
  requestedBy: string;
  requestDate: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  adminNotes?: string;
}

interface AppState {
  apps: App[];
  requests: AppRequest[];
  categories: AppCategory[];
  searchTerm: string;
  selectedCategory: string;
  sortBy: string;
  viewMode: 'card' | 'list';
  currentPage: number;
  itemsPerPage: number;
  isLoading: boolean;
  error: string | null;
  showRequestModal: boolean;
  showBuyModal: boolean;
  showTrialModal: boolean;
  selectedApp: App | null;
}

interface AppActions {
  setApps: (apps: App[]) => void;
  setRequests: (requests: AppRequest[]) => void;
  setCategories: (categories: AppCategory[]) => void;
  setSearchTerm: (term: string) => void;
  setSelectedCategory: (category: string) => void;
  setSortBy: (sortBy: string) => void;
  setViewMode: (mode: 'card' | 'list') => void;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (items: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setShowRequestModal: (show: boolean) => void;
  setShowBuyModal: (show: boolean) => void;
  setShowTrialModal: (show: boolean) => void;
  setSelectedApp: (app: App | null) => void;
  getAppById: (id: string) => App | undefined;
  installApp: (id: string) => void;
  uninstallApp: (id: string) => void;
  startTrial: (id: string) => void;
  purchaseApp: (id: string) => void;
  addRequest: (request: Omit<AppRequest, 'id' | 'requestDate' | 'status'>) => void;
  updateRequestStatus: (id: string, status: AppRequest['status'], adminNotes?: string) => void;
  getFilteredApps: () => App[];
  getPaginatedApps: () => { apps: App[]; totalPages: number; total: number };
  loadInitialData: () => Promise<void>;
  loadCategories: () => Promise<void>;
  refreshApps: () => Promise<void>;
  refreshRequests: () => Promise<void>;
}

// Helper function to transform API app to legacy format
const transformApiApp = (apiApp: any, categories: AppCategory[]): App => {
  const category = categories.find(c => c.app_ctgry_id?.toString() === apiApp.app_ctgry_id?.toString());

  return {
    id: apiApp.app_id?.toString() || '',
    name: apiApp.app_nm,
    description: apiApp.app_desc_tx || '',
    fullDescription: apiApp.app_lng_desc_tx,
    category: category?.app_ctgry_nm || 'Uncategorized',
    rating: parseFloat(apiApp.rtng_nb || apiApp.avg_rtng_nb || '0'),
    reviewCount: parseInt(apiApp.rvw_cnt_nb || apiApp.ttl_rvws_nb || '0'),
    downloads: parseInt(apiApp.dwnld_cnt_nb || apiApp.ttl_dwnlds_nb || '0'),
    accessType: (apiApp.accss_typ_cd || apiApp.app_accs_typ_cd || 'Free') as 'Free' | 'Free Trial' | 'Paid',
    price: parseFloat(apiApp.prce_at || apiApp.app_prce_nb || '0'),
    status: apiApp.app_sts_cd === 'Installed' ? 'Installed' : 'Not Installed',
    coverImage: apiApp.app_lgo_url_tx || apiApp.app_cvr_img_url_tx || apiApp.app_icn_url_tx || apiApp.app_url_tx || '',
    screenshots: apiApp.screenshots?.map((s: any) => s.scrnshot_url_tx),
    launchUrl: apiApp.app_url_tx || apiApp.app_lnch_url_tx,
    isHighlighted: apiApp.is_hghlghtd_in === 1 || apiApp.app_hghlghted_fg === true,
    isActive: apiApp.a_in === 1 || apiApp.app_sts_cd === 'Active',
    lastUpdated: apiApp.lst_updtd_dt || apiApp.u_ts || apiApp.i_ts || new Date().toISOString(),
    version: apiApp.app_vrsn_tx,
    size: apiApp.app_sze_tx,
    developer: apiApp.app_dvlpr_nm,
    features: apiApp.app_ftures_tx ? (typeof apiApp.app_ftures_tx === 'string' ? JSON.parse(apiApp.app_ftures_tx) : apiApp.app_ftures_tx) : [],
    requirements: apiApp.app_rqrmnts_tx ? (typeof apiApp.app_rqrmnts_tx === 'string' ? JSON.parse(apiApp.app_rqrmnts_tx) : apiApp.app_rqrmnts_tx) : []
  };
};

// Helper function to transform API request to legacy format
const transformApiRequest = (apiRequest: ApiAppRequest): AppRequest => {
  return {
    id: apiRequest.rqst_id?.toString() || '',
    appName: apiRequest.app_nm,
    category: apiRequest.app_ctgry_nm || '',
    description: apiRequest.app_desc_tx || '',
    businessJustification: apiRequest.bsnss_jstfctn_tx || '',
    requestedBy: apiRequest.rqstr_nm || '',
    requestDate: apiRequest.rqst_dt || '',
    status: (apiRequest.rqst_sts_cd as 'Pending' | 'Approved' | 'Rejected') || 'Pending',
    adminNotes: apiRequest.admn_ntes_tx
  };
};

export const useAppStore = create<AppState & AppActions>((set, get) => ({
  apps: [],
  requests: [],
  categories: [],
  searchTerm: '',
  selectedCategory: 'All',
  sortBy: 'popular',
  viewMode: 'card',
  currentPage: 1,
  itemsPerPage: 12,
  isLoading: false,
  error: null,
  showRequestModal: false,
  showBuyModal: false,
  showTrialModal: false,
  selectedApp: null,

  setApps: (apps) => set({ apps }),
  setRequests: (requests) => set({ requests }),
  setCategories: (categories) => set({ categories }),
  setSearchTerm: (searchTerm) => set({ searchTerm, currentPage: 1 }),
  setSelectedCategory: (selectedCategory) => set({ selectedCategory, currentPage: 1 }),
  setSortBy: (sortBy) => set({ sortBy }),
  setViewMode: (viewMode) => set({ viewMode }),
  setCurrentPage: (currentPage) => set({ currentPage }),
  setItemsPerPage: (itemsPerPage) => set({ itemsPerPage, currentPage: 1 }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  setShowRequestModal: (showRequestModal) => set({ showRequestModal }),
  setShowBuyModal: (showBuyModal) => set({ showBuyModal }),
  setShowTrialModal: (showTrialModal) => set({ showTrialModal }),
  setSelectedApp: (selectedApp) => set({ selectedApp }),

  getAppById: (id) => {
    const { apps } = get();
    return apps.find(app => app.id === id);
  },

  installApp: (id) => {
    const { apps } = get();
    const updatedApps = apps.map(app =>
      app.id === id
        ? { ...app, status: 'Installed' as const }
        : app
    );
    set({ apps: updatedApps });
  },

  uninstallApp: (id) => {
    const { apps } = get();
    const updatedApps = apps.map(app =>
      app.id === id
        ? { ...app, status: 'Not Installed' as const }
        : app
    );
    set({ apps: updatedApps });
  },

  startTrial: (id) => {
    const { apps } = get();
    const trialExpiryDate = new Date();
    trialExpiryDate.setDate(trialExpiryDate.getDate() + 30);

    const updatedApps = apps.map(app =>
      app.id === id
        ? { ...app, status: 'Installed' as const, trialExpiresAt: trialExpiryDate.toISOString() }
        : app
    );
    set({ apps: updatedApps, showTrialModal: false, selectedApp: null });
  },

  purchaseApp: (id) => {
    const { apps } = get();
    const updatedApps = apps.map(app =>
      app.id === id
        ? { ...app, status: 'Installed' as const, accessType: 'Paid' as const }
        : app
    );
    set({ apps: updatedApps, showBuyModal: false, selectedApp: null });
  },

  addRequest: async (requestData) => {
    try {
      const apiRequest: Partial<ApiAppRequest> = {
        app_nm: requestData.appName,
        app_ctgry_nm: requestData.category,
        app_desc_tx: requestData.description,
        bsnss_jstfctn_tx: requestData.businessJustification,
        rqstr_nm: requestData.requestedBy,
        rqst_sts_cd: 'Pending'
      };

      const savedRequest = await requestService.save(apiRequest as ApiAppRequest);
      const transformedRequest = transformApiRequest(savedRequest);

      const { requests } = get();
      set({ requests: [...requests, transformedRequest], showRequestModal: false });
    } catch (error) {
      console.error('Error adding request:', error);
      set({ error: 'Failed to submit request' });
    }
  },

  updateRequestStatus: async (id, status, adminNotes) => {
    try {
      const apiRequest: Partial<ApiAppRequest> = {
        rqst_id: parseInt(id),
        rqst_sts_cd: status,
        admn_ntes_tx: adminNotes
      };

      await requestService.save(apiRequest as ApiAppRequest);

      const { requests } = get();
      const updatedRequests = requests.map(request =>
        request.id === id
          ? { ...request, status, adminNotes }
          : request
      );
      set({ requests: updatedRequests });
    } catch (error) {
      console.error('Error updating request:', error);
      set({ error: 'Failed to update request' });
    }
  },

  getFilteredApps: () => {
    const { apps, searchTerm, selectedCategory, sortBy } = get();

    let filtered = apps.filter(app => {
      const matchesSearch = app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (app.description?.toLowerCase() || '').includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || app.category === selectedCategory;
      return matchesSearch && matchesCategory && app.isActive !== false;
    });

    switch (sortBy) {
      case 'popular':
        return filtered.sort((a, b) => b.reviewCount - a.reviewCount);
      case 'rating':
        return filtered.sort((a, b) => b.rating - a.rating);
      case 'name':
        return filtered.sort((a, b) => a.name.localeCompare(b.name));
      case 'newest':
        return filtered.sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime());
      default:
        return filtered;
    }
  },

  getPaginatedApps: () => {
    const { currentPage, itemsPerPage } = get();
    const filteredApps = get().getFilteredApps();
    const total = filteredApps.length;
    const totalPages = Math.ceil(total / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const apps = filteredApps.slice(startIndex, startIndex + itemsPerPage);

    return { apps, totalPages, total };
  },

  loadCategories: async () => {
    try {
      const categories = await appCategoryService.getAll();
      set({ categories });
    } catch (error) {
      console.error('Error loading categories:', error);
      set({ error: 'Failed to load categories' });
    }
  },

  refreshApps: async () => {
    set({ isLoading: true, error: null });
    try {
      const { categories } = get();
      const apiApps = await appService.getAll();
      const transformedApps = apiApps.map(app => transformApiApp(app, categories));
      set({ apps: transformedApps, isLoading: false });
    } catch (error) {
      console.error('Error loading apps:', error);
      set({ error: 'Failed to load apps', isLoading: false });
    }
  },

  refreshRequests: async () => {
    try {
      const apiRequests = await requestService.getAll();
      const transformedRequests = apiRequests.map(transformApiRequest);
      set({ requests: transformedRequests });
    } catch (error) {
      console.error('Error loading requests:', error);
      set({ error: 'Failed to load requests' });
    }
  },

  loadInitialData: async () => {
    set({ isLoading: true, error: null });
    try {
      // Load categories first
      const categories = await appCategoryService.getAll();
      set({ categories });

      // Load apps with category data
      const apiApps = await appService.getAll();
      const transformedApps = apiApps.map(app => transformApiApp(app, categories));

      // Load requests
      const apiRequests = await requestService.getAll();
      const transformedRequests = apiRequests.map(transformApiRequest);

      set({
        apps: transformedApps,
        requests: transformedRequests,
        isLoading: false
      });
    } catch (error) {
      console.error('Error loading initial data:', error);
      set({
        error: 'Failed to load data. Please try again.',
        isLoading: false
      });
    }
  }
}));
